import React, { useState, useEffect } from "react";
import axios from "axios";
import { format } from "date-fns";
import { CalendarIcon, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { TIME_SLOTS } from "@/lib/content";
import { WHATSAPP_LINK, PHONE } from "@/lib/site";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingDialog({ open, onOpenChange }) {
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    preferred_date: undefined,
    preferred_time: "",
    session_mode: "online",
    message: "",
  });

  // Pre-fill name/email when a signed-in user opens the dialog.
  useEffect(() => {
    if (open && user) {
      setForm((p) => ({
        ...p,
        name: p.name || user.name || "",
        email: p.email || user.email || "",
      }));
    }
  }, [open, user]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.preferred_date || !form.preferred_time) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        preferred_date: format(form.preferred_date, "yyyy-MM-dd"),
        preferred_time: form.preferred_time,
        session_mode: form.session_mode,
        message: form.message.trim(),
        user_id: user?.user_id || null,
      };
      await axios.post(`${API}/bookings`, payload);
      toast.success("Your session request has been received. We will reach out soon.");
      // Open WhatsApp prefilled
      const wa = `https://wa.me/91${PHONE}?text=${encodeURIComponent(
        `Hello, I just booked a session.\nName: ${payload.name}\nDate: ${payload.preferred_date}\nTime: ${payload.preferred_time}\nMode: ${payload.session_mode}`
      )}`;
      window.open(wa, "_blank", "noopener,noreferrer");
      onOpenChange(false);
      setForm({
        name: "",
        phone: "",
        email: "",
        preferred_date: undefined,
        preferred_time: "",
        session_mode: "online",
        message: "",
      });
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="booking-dialog"
        className="max-w-lg bg-[#FDFBF7] border-brand-borderSoft"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl text-brand-teal">
            Book a Session
          </DialogTitle>
          <DialogDescription className="text-brand-tealMuted font-sans">
            Share your details and a preferred time. We will confirm via WhatsApp or call.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="bk-name" className="text-brand-teal text-xs tracking-widest uppercase">Name</Label>
              <Input
                id="bk-name"
                data-testid="booking-form-name"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Your name"
                className="bg-white border-brand-borderSoft focus-visible:ring-brand-gold"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bk-phone" className="text-brand-teal text-xs tracking-widest uppercase">Phone</Label>
              <Input
                id="bk-phone"
                data-testid="booking-form-phone"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="Mobile number"
                className="bg-white border-brand-borderSoft focus-visible:ring-brand-gold"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bk-email" className="text-brand-teal text-xs tracking-widest uppercase">Email</Label>
            <Input
              id="bk-email"
              type="email"
              data-testid="booking-form-email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="you@email.com"
              className="bg-white border-brand-borderSoft focus-visible:ring-brand-gold"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-brand-teal text-xs tracking-widest uppercase">Preferred date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="booking-form-date-trigger"
                    className="w-full justify-start bg-white border-brand-borderSoft text-brand-teal font-sans hover:bg-brand-cream"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-brand-tealMuted" strokeWidth={1.5} />
                    {form.preferred_date ? format(form.preferred_date, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white border-brand-borderSoft" align="start">
                  <Calendar
                    mode="single"
                    selected={form.preferred_date}
                    onSelect={(d) => update("preferred_date", d)}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-1.5">
              <Label className="text-brand-teal text-xs tracking-widest uppercase">Preferred time</Label>
              <Select value={form.preferred_time} onValueChange={(v) => update("preferred_time", v)}>
                <SelectTrigger
                  data-testid="booking-form-time-trigger"
                  className="bg-white border-brand-borderSoft text-brand-teal"
                >
                  <SelectValue placeholder="Select a slot" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {TIME_SLOTS.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`booking-form-time-${t}`}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-brand-teal text-xs tracking-widest uppercase">Mode of session</Label>
            <RadioGroup
              value={form.session_mode}
              onValueChange={(v) => update("session_mode", v)}
              className="flex gap-6"
            >
              <label className="flex items-center gap-2 cursor-pointer">
                <RadioGroupItem value="online" id="mode-online" data-testid="booking-form-mode-online" />
                <span className="font-sans text-brand-teal">Online</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <RadioGroupItem value="offline" id="mode-offline" data-testid="booking-form-mode-offline" />
                <span className="font-sans text-brand-teal">Offline</span>
              </label>
            </RadioGroup>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bk-msg" className="text-brand-teal text-xs tracking-widest uppercase">
              A note (optional)
            </Label>
            <Textarea
              id="bk-msg"
              data-testid="booking-form-message"
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              placeholder="Anything you would like us to know…"
              rows={3}
              className="bg-white border-brand-borderSoft focus-visible:ring-brand-gold"
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => window.open(WHATSAPP_LINK, "_blank", "noopener,noreferrer")}
              data-testid="booking-form-whatsapp"
              className="text-brand-tealMuted hover:bg-brand-cream"
            >
              Chat on WhatsApp
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="booking-form-submit"
              className="bg-brand-teal text-brand-bg hover:bg-brand-tealMuted rounded-full px-6"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" strokeWidth={1.5} /> Request session
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
